import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

interface RightPanelProps {
    children: React.ReactNode;
    isExtended: boolean;
    moveBelowViewOnMobile: boolean;
}

const useRightPanelScrollbarSync = (
    panelRef: React.RefObject<HTMLDivElement | null>,
    contentRef: React.RefObject<HTMLDivElement | null>,
    scrollbarRef: React.RefObject<HTMLDivElement | null>,
    spacerRef: React.RefObject<HTMLDivElement | null>
) => {
    const [hasScrollableOverflow, setHasScrollableOverflow] = useState(false);

    useLayoutEffect(() => {
        const panelElement = panelRef.current;
        const contentElement = contentRef.current;
        const scrollbarElement = scrollbarRef.current;
        const spacerElement = spacerRef.current;

        if (!panelElement || !contentElement || !scrollbarElement || !spacerElement) {
            return;
        }

        let syncSource: 'panel' | 'scrollbar' | null = null;
        let animationFrameId: number | null = null;

        const syncScrollbarMetrics = () => {
            const scrollHeight = panelElement.scrollHeight;
            const hasOverflow = scrollHeight > panelElement.clientHeight + 1;

            spacerElement.style.height = `${scrollHeight}px`;
            setHasScrollableOverflow(previous =>
                previous === hasOverflow ? previous : hasOverflow
            );

            if (scrollbarElement.scrollTop !== panelElement.scrollTop) {
                scrollbarElement.scrollTop = panelElement.scrollTop;
            }
        };

        const scheduleMetricSync = () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }

            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                syncScrollbarMetrics();
            });
        };

        const syncFromPanel = () => {
            if (syncSource === 'scrollbar') {
                syncSource = null;
                return;
            }

            if (scrollbarElement.scrollTop === panelElement.scrollTop) {
                return;
            }

            syncSource = 'panel';
            scrollbarElement.scrollTop = panelElement.scrollTop;
        };

        const syncFromScrollbar = () => {
            if (panelElement.scrollHeight <= panelElement.clientHeight + 1) {
                return;
            }

            if (syncSource === 'panel') {
                syncSource = null;
                return;
            }

            if (panelElement.scrollTop === scrollbarElement.scrollTop) {
                return;
            }

            syncSource = 'scrollbar';
            panelElement.scrollTop = scrollbarElement.scrollTop;
        };

        syncScrollbarMetrics();

        const resizeObserver =
            typeof ResizeObserver === 'undefined'
                ? undefined
                : new ResizeObserver(() => {
                      scheduleMetricSync();
                  });

        resizeObserver?.observe(contentElement);
        panelElement.addEventListener('scroll', syncFromPanel, { passive: true });
        scrollbarElement.addEventListener('scroll', syncFromScrollbar, { passive: true });
        window.addEventListener('resize', scheduleMetricSync);

        return () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }

            resizeObserver?.disconnect();
            panelElement.removeEventListener('scroll', syncFromPanel);
            scrollbarElement.removeEventListener('scroll', syncFromScrollbar);
            window.removeEventListener('resize', scheduleMetricSync);
        };
    }, [panelRef, contentRef, scrollbarRef, spacerRef]);

    return hasScrollableOverflow;
};

const RightPanel = ({ children, isExtended, moveBelowViewOnMobile }: RightPanelProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const scrollbarRef = useRef<HTMLDivElement>(null);
    const scrollbarSpacerRef = useRef<HTMLDivElement>(null);
    const hasScrollableOverflow = useRightPanelScrollbarSync(
        panelRef,
        contentRef,
        scrollbarRef,
        scrollbarSpacerRef
    );

    useLayoutEffect(() => {
        const updateMapControlOffset = () => {
            const scrollbarWidth = hasScrollableOverflow
                ? (scrollbarRef.current?.getBoundingClientRect().width ?? 0)
                : 0;
            document.documentElement.style.setProperty(
                '--right-panel-map-control-offset',
                `${scrollbarWidth}px`
            );
        };

        updateMapControlOffset();
        window.addEventListener('resize', updateMapControlOffset);

        return () => {
            window.removeEventListener('resize', updateMapControlOffset);
            document.documentElement.style.setProperty(
                '--right-panel-map-control-offset',
                '0px'
            );
        };
    }, [hasScrollableOverflow]);

    const panelClassName = [
        'c-right-panel',
        isExtended ? 'c-right-panel--extended' : '',
        moveBelowViewOnMobile ? 't-right-panel-below-view-mobile' : '',
    ]
        .filter(Boolean)
        .join(' ');
    const panelContentClassName = `c-right-panel__content${
        hasScrollableOverflow ? ' c-right-panel__content--offset' : ''
    }`;

    return (
        <div className={panelClassName} ref={panelRef}>
            <div
                className={`c-right-panel__scrollbar${
                    hasScrollableOverflow ? '' : ' c-right-panel__scrollbar--inactive'
                }`}
                ref={scrollbarRef}
                aria-hidden="true"
            >
                <div
                    className="c-right-panel__scrollbar-spacer"
                    ref={scrollbarSpacerRef}
                />
            </div>
            <div className={panelContentClassName}>
                <div className="c-right-panel__inner-content" ref={contentRef}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default RightPanel;
